"use client";

import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Input, type InputProps } from "./input";

interface FormattedNumberInputProps
  extends Omit<InputProps, "value" | "onChange"> {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  prefix?: string;
  suffix?: string;
  allowDecimal?: boolean;
  maxDecimals?: number;
}

export function FormattedNumberInput({
  value,
  onChange,
  prefix = "",
  suffix = "",
  allowDecimal = true,
  maxDecimals = 2,
  className,
  ...props
}: FormattedNumberInputProps) {
  const [localValue, setLocalValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(formatValue(value));
    }
  }, [value, isFocused]);

  const formatValue = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return "";

    const formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: allowDecimal ? maxDecimals : 0,
      useGrouping: true,
    });

    return formatter.format(val);
  };

  const parseValue = (input: string): number | null => {
    if (input === "" || input === ".") return null;

    const cleanInput = input.replace(/,/g, "");

    if (allowDecimal) {
      const parsed = Number.parseFloat(cleanInput);
      return Number.isNaN(parsed) ? null : parsed;
    }

    const parsed = Number.parseInt(cleanInput);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const formatInput = (input: string): string => {
    const cleanInput = input.replace(/,/g, "");

    if (allowDecimal) {
      if (cleanInput.endsWith(".")) {
        const [wholePart] = cleanInput.split(".");
        const parsed = parseValue(wholePart);
        return parsed === null ? cleanInput : `${formatValue(parsed)}.`;
      }

      if (cleanInput.includes(".")) {
        const [wholePart, decimalPart] = cleanInput.split(".");
        const parsed = parseValue(wholePart);
        return parsed === null
          ? cleanInput
          : `${formatValue(parsed)}.${decimalPart}`;
      }
    }

    const parsed = parseValue(cleanInput);
    return parsed === null ? cleanInput : formatValue(parsed);
  };

  return (
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {prefix}
        </span>
      )}
      <Input
        value={localValue}
        onChange={(e) => {
          const rawInput = e.target.value;

          if (allowDecimal) {
            const decimalCount = (rawInput.match(/\./g) || []).length;
            if (decimalCount > 1) return;

            const parts = rawInput.split(".");
            if (parts[1] && parts[1].length > maxDecimals) return;
          }

          const formattedInput = formatInput(rawInput);
          setLocalValue(formattedInput);
          const newValue = parseValue(rawInput);
          onChange(newValue);
        }}
        onFocus={() => {
          setIsFocused(true);
          setLocalValue(localValue);
        }}
        onBlur={() => {
          setIsFocused(false);
          setLocalValue(formatValue(value));
        }}
        className={cn(prefix && "pl-7", suffix && "pr-7", className)}
        {...props}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {suffix}
        </span>
      )}
    </div>
  );
}
